import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Target, ListChecks, FileText, User, Building2 } from "lucide-react";
import { useApi, fetchProject } from "../api";
import { StatusPill, PriorityPill, HealthPill, ProgressBar, Avatar, Loading, ErrorState, formatDate } from "../components/common";
import TaskDetailSheet from "../components/TaskDetailSheet";
import ChatPanel from "../components/ChatPanel";
import { MessagesSquare } from "lucide-react";

function Info({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-400"><Icon className="h-4 w-4" /> {label}</div>
      <p className="mt-1.5 text-sm font-bold text-slate-900">{value}</p>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi(() => fetchProject(id), [id]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const openTask = (tid) => { setSelectedTask(tid); setSheetOpen(true); };

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (loading || !data) return <Loading />;
  const { project: p, tasks, documents } = data;

  return (
    <div className="mx-auto max-w-6xl animate-fade-in-up">
      <button onClick={() => navigate("/projects")} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Back to Projects</button>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold text-red-600">{p.code}</p>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{p.name}</h2>
            {p.description && <p className="mt-2 max-w-2xl text-sm text-slate-500">{p.description}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2"><StatusPill value={p.status} /><PriorityPill value={p.priority} /><HealthPill value={p.health} /></div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-slate-900">{p.progress}%</p>
            <p className="text-xs text-slate-400">complete</p>
            <div className="mt-2 w-40"><ProgressBar value={p.progress} /></div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Info icon={Calendar} label="Start Date" value={formatDate(p.startDate)} />
        <Info icon={Target} label="Due Date" value={formatDate(p.dueDate)} />
        <Info icon={ListChecks} label="Tasks" value={`${p.tasksDone} / ${p.tasksTotal} done`} />
        <Info icon={Building2} label="Customer" value={p.customer || "—"} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-base font-bold text-slate-900">Tasks ({tasks.length})</h3>
            <div className="space-y-2">
              {tasks.map((t) => (
                <div key={t.id} onClick={() => openTask(t.id)} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-100 p-3 transition-colors hover:bg-slate-50">
                  <Avatar avatar={t.assigneeAvatar} size={30} />
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-800">{t.title}</p><p className="text-xs text-slate-400">Due {formatDate(t.dueDate)}{t.phase ? ` · ${t.phase}` : ""}</p></div>
                  <PriorityPill value={t.priority} /><StatusPill value={t.status} />
                </div>
              ))}
              {tasks.length === 0 && <p className="text-sm text-slate-400">No tasks for this project.</p>}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-base font-bold text-slate-900">Documents ({documents.length})</h3>
            <div className="space-y-2">
              {documents.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-xl border border-slate-100 p-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50"><FileText className="h-4 w-4 text-red-600" /></div>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-800">{d.name}</p><p className="text-xs text-slate-400">{d.category}</p></div>
                </div>
              ))}
              {documents.length === 0 && <p className="text-sm text-slate-400">No documents for this project.</p>}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900"><MessagesSquare className="h-4 w-4 text-[#d0333a]" /> Project Discussion</h3>
            <ChatPanel scope="project" refId={p.id} refName={p.name} ownerId={p.pmId} />
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-slate-900"><User className="h-4 w-4 text-slate-400" /> Project Manager</h3>
            <div className="flex items-center gap-3"><Avatar avatar={p.pmAvatar} size={44} /><div><p className="text-sm font-bold text-slate-900">{p.pm || "Unassigned"}</p><p className="text-xs text-slate-400">Project Manager</p></div></div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-3 text-base font-bold text-slate-900">Summary</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Open tasks</dt><dd className="font-semibold text-slate-800">{tasks.filter((t) => t.status !== "Complete" && t.status !== "Completed").length}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Overdue</dt><dd className="font-semibold text-slate-800">{p.overdueTasks}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Documents</dt><dd className="font-semibold text-slate-800">{documents.length}</dd></div>
            </dl>
          </div>
        </div>
      </div>

      <TaskDetailSheet taskId={selectedTask} open={sheetOpen} onOpenChange={setSheetOpen} onSaved={reload} />
    </div>
  );
}
